import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Drawer, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { QUERY_NO_UPDATE_DETAIL_LIST } from '../../../../graphql/queries/dashboard/queryNoUpdateDetailList';
import {
    NoUpdateDashboardDrillTypeInput,
    NoUpdateDetailPageRequestInput,
    NoUpdateDetailRow,
    NoUpdateStatusInput,
    NoUpdateTypeGql,
    QueryNoUpdateDetailListResponse,
} from '../../../../graphql/types/dashboard/NoUpdateDashboardTypes';
import {
    GENERATE_NO_UPDATE_DETAIL_DOWNLOAD_REPORT,
    GenerateNoUpdateDetailDownloadReportResponse,
    GenerateNoUpdateDetailDownloadReportVariables,
} from '../../../../graphql/mutations/dashboard/generateNoUpdateDetailDownloadReport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../../components/asyncReportDownload';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { getUserInfo } from '../../../../utils/auth';
import { showError } from '../../../../utils/exceptionUtils';
import { PACIFIC_TIMEZONE, formatUtcToPacific } from '../../../../utils/timezoneFormatter';
import { stripOrderEventDataPrefix } from '../../../../constants/externalEventCodes';

export interface HubOptionForNoUpdateDetail {
    id: number;
    name: string;
}

interface NoUpdateDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    drillContext: {
        drillType: NoUpdateDashboardDrillTypeInput;
        hubId: number;
        startDate: string;
        endDate: string;
        metricLabel: string;
        /** 按"责任 hub"下钻（点 干线运输 L3 行 时传） */
        responsibleHubId?: number | null;
        /** 按"责任 whs"下钻（点 尾程 L3 行 时传） */
        responsibleWarehouseId?: number | null;
    } | null;
    noUpdateStatus: NoUpdateStatusInput;
    /** 主页返回的 HUB 列表，用于全美场景下的 HUB 多选筛选；不传则从当前页明细中提取 */
    hubList?: HubOptionForNoUpdateDetail[];
}

const PAGE_SIZE_DEFAULT = 10;

const NO_UPDATE_TYPE_LABEL_KEY: Record<NoUpdateTypeGql, string> = {
    [NoUpdateTypeGql.LINEHAUL_INBOUND]: 'linehaulInbound',
    [NoUpdateTypeGql.IN_HUB_OPERATION]: 'inHubOperation',
    [NoUpdateTypeGql.IN_HUB_LINEHAUL]: 'inHubLinehaul',
    [NoUpdateTypeGql.LAST_MILE]: 'lastMile',
};

const NO_UPDATE_TYPE_TAG_COLOR: Record<NoUpdateTypeGql, string> = {
    [NoUpdateTypeGql.LINEHAUL_INBOUND]: 'blue',
    [NoUpdateTypeGql.IN_HUB_OPERATION]: 'green',
    [NoUpdateTypeGql.IN_HUB_LINEHAUL]: 'orange',
    [NoUpdateTypeGql.LAST_MILE]: 'purple',
};

const NoUpdateDetailDrawer: React.FC<NoUpdateDetailDrawerProps> = ({
    open,
    onClose,
    drillContext,
    noUpdateStatus,
    hubList,
}) => {
    const { t } = useTranslation();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [selectedHubIds, setSelectedHubIds] = useState<number[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    // Long 经 JSON 可能为字符串 "0"：与数字统一，避免 isAllUs 为 false 但请求仍按 hubId=0
    const safeHubId = useMemo(() => {
        const n = Number(drillContext?.hubId ?? 0);
        return Number.isFinite(n) ? n : 0;
    }, [drillContext?.hubId]);
    const isAllUs = safeHubId === 0;
    const safeSelectedHubIds = useMemo(
        () => (selectedHubIds ?? []).map((v) => Number(v)).filter((v) => Number.isFinite(v)),
        [selectedHubIds]
    );

    // 抽屉关闭时清空状态，避免下次打开沿用上次选择
    useEffect(() => {
        if (!open) {
            setSelectedRowKeys([]);
            setSelectedHubIds([]);
            setPage(1);
        }
    }, [open]);

    const input: NoUpdateDetailPageRequestInput | null = useMemo(() => {
        if (!drillContext) return null;
        return {
            statDateStart: drillContext.startDate,
            statDateEnd: drillContext.endDate,
            hubId: safeHubId,
            hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
            drillType: drillContext.drillType,
            noUpdateStatus,
            page,
            size: pageSize,
            sortBy: 'noUpdateHours',
            sortDirection: 'DESC',
            responsibleHubId: drillContext.responsibleHubId ?? null,
            responsibleWarehouseId: drillContext.responsibleWarehouseId ?? null,
        };
    }, [drillContext, noUpdateStatus, page, pageSize, safeHubId, safeSelectedHubIds]);

    const { data, loading } = useQuery<QueryNoUpdateDetailListResponse>(QUERY_NO_UPDATE_DETAIL_LIST, {
        variables: input ? { input } : undefined,
        skip: !open || !input,
        fetchPolicy: 'cache-and-network',
    });

    const content = data?.queryNoUpdateDetailList?.content ?? [];
    const total = data?.queryNoUpdateDetailList?.totalElements ?? 0;

    const [generateReportMutation] = useMutation<
        GenerateNoUpdateDetailDownloadReportResponse,
        GenerateNoUpdateDetailDownloadReportVariables
    >(GENERATE_NO_UPDATE_DETAIL_DOWNLOAD_REPORT);

    /** 全美场景的 hub 下拉选项：优先用主页传入的 hubList，否则从当前页明细兜底 */
    const hubOptions = useMemo(() => {
        if (hubList && hubList.length > 0) {
            return hubList.map((hub) => ({ label: hub.name, value: hub.id }));
        }
        const seen = new Map<number, string>();
        content.forEach((row) => {
            if (row.hubId != null && row.hubName?.trim()) seen.set(row.hubId, row.hubName.trim());
        });
        return Array.from(seen.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([id, name]) => ({ label: name, value: id }));
    }, [hubList, content]);

    const copyTrackingNumbers = useCallback(async () => {
        const rows = selectedRowKeys.length > 0
            ? content.filter((r) => selectedRowKeys.includes(r.id))
            : content;
        const numbers = rows.map((r) => r.trackingNumber).filter((n) => n && n.length > 0);
        if (numbers.length === 0) {
            message.warning(t('common.messages.noDataToCopy', '没有可复制的数据'));
            return;
        }
        try {
            await navigator.clipboard.writeText(numbers.join('\n'));
            message.success(t('common.messages.copied'));
        } catch (err) {
            console.error('Copy tracking numbers failed:', err);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRowKeys, content, t]);

    const handleDownloadDetails = useCallback(async () => {
        if (!drillContext) return;
        if (exportLoading) {
            message.warning(t('wms.dashboard.noUpdate.drawer.exportLoading'));
            return;
        }
        await runExportWithCooldown(async () => {
            try {
                // 看板下钻明细展示固定走美西时间（formatUtcToPacific），导出时间也对齐美西；
                // WMS 断更明细仍走用户本地时区，互不影响。
                const filter = {
                    statDateStart: drillContext.startDate,
                    statDateEnd: drillContext.endDate,
                    hubId: safeHubId,
                    hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
                    drillType: drillContext.drillType,
                    noUpdateStatus,
                    responsibleHubId: drillContext.responsibleHubId ?? null,
                    responsibleWarehouseId: drillContext.responsibleWarehouseId ?? null,
                    ids: selectedRowKeys.length > 0 ? selectedRowKeys.map((k) => String(k)) : null,
                };
                const response = await generateReportMutation({
                    variables: { filter, timezone: PACIFIC_TIMEZONE },
                });
                const fileName = response.data?.generateNoUpdateDetailDownloadReport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error(t('wms.dashboard.noUpdate.drawer.exportFailed'));
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [
        drillContext,
        exportLoading,
        runExportWithCooldown,
        safeHubId,
        safeSelectedHubIds,
        noUpdateStatus,
        selectedRowKeys,
        generateReportMutation,
        t,
    ]);

    const columns: ColumnsType<NoUpdateDetailRow> = useMemo(() => {
        const C = 'wms.dashboard.noUpdate.column';
        const TAG = 'wms.dashboard.noUpdate.tag';
        const cols: ColumnsType<NoUpdateDetailRow> = [
            {
                title: t(`${C}.trackingNumber`),
                dataIndex: 'trackingNumber',
                key: 'trackingNumber',
                width: 180,
                fixed: 'left',
                render: (text: string, record: NoUpdateDetailRow) => {
                    const tn = text ?? record.trackingNumber ?? '-';
                    const oid = record.orderId;
                    if (oid == null || oid === 0) return <span>{tn}</span>;
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedOrderId(String(oid));
                                setParcelDetailVisible(true);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {tn}
                        </a>
                    );
                },
            },
            {
                title: t(`${C}.parcelStatus`),
                dataIndex: 'parcelStatus',
                key: 'parcelStatus',
                width: 130,
                fixed: 'left',
                render: (v: string | null | undefined) =>
                    v ? t(`enum.overallStatus.${v}`, { defaultValue: v }) : '-',
            },
        ];
        const sortingSeg = (row: NoUpdateDetailRow, idx: 0 | 1 | 2): string => {
            const parts = row.sortingCode?.split('-') ?? [];
            return parts[idx] || '-';
        };
        cols.push(
            { title: t(`${C}.bagNumber`), dataIndex: 'bagNumber', key: 'bagNumber', width: 140, render: (v) => v ?? '-' },
            { title: t(`${C}.responsibleHub`), dataIndex: 'hubName', key: 'hubName', width: 120, render: (v) => v ?? '-' },
            { title: t(`${C}.destinationHub`), key: 'destinationHub', width: 110, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 0) },
            { title: t(`${C}.destinationWarehouse`), key: 'destinationWarehouse', width: 140, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 1) },
            { title: t(`${C}.destinationRoute`), key: 'destinationRoute', width: 130, render: (_: unknown, row: NoUpdateDetailRow) => sortingSeg(row, 2) },
            { title: t(`${C}.dspName`), dataIndex: 'dspName', key: 'dspName', width: 120, render: (v) => v ?? '-' },
            { title: t(`${C}.driverName`), dataIndex: 'driverName', key: 'driverName', width: 120, render: (v) => v ?? '-' },
            {
                title: t(`${C}.lastTrackingEvent`),
                dataIndex: 'lastTrackingEventName',
                key: 'lastTrackingEventName',
                width: 140,
                render: (v: string | null | undefined) =>
                    v ? t(`enum.orderEvent.${stripOrderEventDataPrefix(v)}`, { defaultValue: v }) : '-',
            },
            {
                title: t(`${C}.lastTrackingAccount`),
                key: 'lastTrackingAccount',
                width: 180,
                render: (_: unknown, row: NoUpdateDetailRow) => {
                    if (!row.lastTrackingAccountName) return '-';
                    return `${row.lastTrackingAccountName}${row.lastTrackingAccountRole ? `(${row.lastTrackingAccountRole})` : ''}`;
                },
            },
            { title: t(`${C}.lastTrackingTime`), dataIndex: 'lastTrackingTime', key: 'lastTrackingTime', width: 170, render: (v) => formatUtcToPacific(v) },
            {
                title: t(`${C}.type`),
                key: 'noUpdateType',
                width: 140,
                render: (_: unknown, row: NoUpdateDetailRow) => {
                    if (!row.noUpdateType) return '-';
                    const label = t(
                        `wms.dashboard.noUpdate.type.${NO_UPDATE_TYPE_LABEL_KEY[row.noUpdateType] ?? row.noUpdateType}`,
                        { defaultValue: row.noUpdateType },
                    );
                    return <Tag color={NO_UPDATE_TYPE_TAG_COLOR[row.noUpdateType]}>{label}</Tag>;
                },
            },
            { title: t(`${C}.responsibleSite`), dataIndex: 'responsibleSiteName', key: 'responsibleSiteName', width: 140, render: (v) => v ?? '-' },
            { title: t(`${C}.location`), dataIndex: 'noUpdateLocationName', key: 'noUpdateLocationName', width: 140, render: (v) => v ?? '-' },
            {
                title: t(`${C}.noUpdateStartEvent`),
                dataIndex: 'noUpdateStartEventName',
                key: 'noUpdateStartEventName',
                width: 140,
                render: (v: string | null | undefined) =>
                    v ? t(`enum.orderEvent.${stripOrderEventDataPrefix(v)}`, { defaultValue: v }) : '-',
            },
            {
                title: t(`${C}.noUpdateStartAccount`),
                key: 'noUpdateStartAccount',
                width: 180,
                render: (_: unknown, row: NoUpdateDetailRow) => {
                    if (!row.noUpdateStartAccountName) return '-';
                    return `${row.noUpdateStartAccountName}${row.noUpdateStartAccountRole ? `(${row.noUpdateStartAccountRole})` : ''}`;
                },
            },
            { title: t(`${C}.noUpdateStartTime`), dataIndex: 'noUpdateStartTime', key: 'noUpdateStartTime', width: 170, render: (v) => formatUtcToPacific(v) },
            {
                title: t(`${C}.noUpdateEndTime`),
                dataIndex: 'noUpdateEndTime',
                key: 'noUpdateEndTime',
                width: 170,
                render: (v) => (v ? formatUtcToPacific(v) : t(`${TAG}.currentlyNoUpdate`)),
            },
            { title: t(`${C}.noUpdateHours`), dataIndex: 'noUpdateHours', key: 'noUpdateHours', width: 110 },
            {
                title: t(`${C}.isCurrentlyNoUpdate`),
                dataIndex: 'isCurrentlyNoUpdate',
                key: 'isCurrentlyNoUpdate',
                width: 100,
                render: (v: number) => {
                    if (v === 1) return <Tag color="blue">{t(`${TAG}.currentlyNoUpdate`)}</Tag>;
                    if (v === -1) return <Tag color="orange">{t(`${TAG}.beforePenalty`)}</Tag>;
                    return <Tag>{t(`${TAG}.everNoUpdate`)}</Tag>;
                },
            }
        );
        return cols;
    }, [t]);

    const drawerTitleWithNote = (
        <>
            {drillContext ? (
                <span>
                    {t('wms.dashboard.noUpdate.drawer.title')} - <Typography.Text strong>{drillContext.metricLabel}</Typography.Text>
                    <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                        {drillContext.startDate} ~ {drillContext.endDate}
                    </Typography.Text>
                </span>
            ) : (
                <span>{t('wms.dashboard.noUpdate.drawer.title')}</span>
            )}
            <span className="text-sm font-normal ml-3 text-gray-500">
                {t('wms.dashboard.noUpdate.drawer.westUsTimeNote')}
            </span>
        </>
    );

    return (
        <Drawer title={drawerTitleWithNote} width={1200} open={open} onClose={onClose} destroyOnClose>
            {isAllUs && (
                <Row gutter={[16, 16]} className="mb-4">
                    <Col>
                        <span className="mr-2">{t('wms.dashboard.noUpdate.drawer.hubLabel')}</span>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder={t('wms.dashboard.noUpdate.drawer.hubPlaceholder')}
                            style={{ minWidth: 220, maxWidth: 400 }}
                            value={selectedHubIds}
                            onChange={(v) => {
                                setSelectedHubIds(v ?? []);
                                setPage(1);
                                setSelectedRowKeys([]);
                            }}
                            options={hubOptions}
                            showSearch
                            filterOption={(inputVal, opt) =>
                                (opt?.label ?? '').toString().toLowerCase().includes(inputVal.toLowerCase())
                            }
                        />
                    </Col>
                </Row>
            )}
            <Row gutter={[16, 4]} className="mb-1" justify="end" align="middle">
                <Col>
                    <Space>
                        {selectedRowKeys.length > 0 && (
                            <span className="text-gray-500 text-sm">
                                {t('wms.dashboard.noUpdate.drawer.selectedRowsCount', { count: selectedRowKeys.length })}
                            </span>
                        )}
                        <Button type="primary" onClick={() => void copyTrackingNumbers()}>
                            {t('common.buttons.copyTracking', '复制单号')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => void handleDownloadDetails()}
                            loading={exportLoading}
                            disabled={exportLoading}
                        >
                            {getExportButtonLabel(
                                selectedRowKeys.length > 0
                                    ? t('wms.dashboard.noUpdate.drawer.downloadSelected')
                                    : t('wms.dashboard.noUpdate.drawer.download')
                            )}
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Table<NoUpdateDetailRow>
                rowKey="id"
                columns={columns}
                dataSource={content}
                loading={loading}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                }}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
            />
            <TablePaginationBar
                className="mt-3"
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
            />
            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name ?? ''}
                onClose={() => setParcelDetailVisible(false)}
            />
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.quality}
            />
        </Drawer>
    );
};

export default NoUpdateDetailDrawer;
